import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';

const EVIDENCE_OPTIONS = [
  { value: '', label: 'Not specified' },
  { value: 'phone_call', label: 'Phone call' },
  { value: 'email', label: 'Email' },
  { value: 'documentation', label: 'Documentation' },
  { value: 'other', label: 'Other' },
];

const buildRatingOptions = (currentRating) => {
  const minRating = Number.isFinite(Number(currentRating))
    ? Math.min(100, Math.max(0, Number(currentRating)))
    : 0;

  // The CURRENT rating is offered too (when it is a real selectable rating):
  // confirming "no change" is how a manual review gets resolved when the
  // client's rating genuinely did not move. The server bills nothing for it.
  const options = [];
  for (let rating = 10; rating <= 100; rating += 10) {
    if (rating >= minRating) options.push(rating);
  }
  return options;
};

const formatClaimStatus = (status) => {
  if (!status) return 'not set';
  return String(status)
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const ConfirmRatingModal = ({
  show,
  onHide,
  claim,
  currentRating = null,
  onConfirm,
  confirming = false,
}) => {
  const isOutsideSubmittedStatus =
    String(claim?.claimStatus || '').trim().toLowerCase() !== 'submitted to va';
  const ratingOptions = useMemo(
    () => buildRatingOptions(currentRating),
    [currentRating],
  );
  const currentRatingNumber = Number.isFinite(Number(currentRating))
    ? Number(currentRating)
    : null;
  const [newRating, setNewRating] = useState('');
  const [evidenceType, setEvidenceType] = useState('');
  const [evidenceNote, setEvidenceNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!show) return;
    // Default to the first INCREASE (the historical behavior) so nobody
    // accidentally saves "no change" by clicking through; the current rating
    // stays selectable above it for the deliberate confirm-unchanged case.
    const firstIncrease = ratingOptions.find(
      (rating) => currentRatingNumber == null || rating > currentRatingNumber,
    );
    const fallback = ratingOptions[0];
    setNewRating(
      firstIncrease != null
        ? String(firstIncrease)
        : fallback != null
          ? String(fallback)
          : '',
    );
    setEvidenceType('');
    setEvidenceNote('');
    setConfirmed(false);
  }, [show, ratingOptions, currentRatingNumber]);

  const confirmingNoChange =
    currentRatingNumber != null && Number(newRating) === currentRatingNumber;

  const canSubmit = Boolean(newRating) && confirmed && !confirming;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    onConfirm({
      newRating: Number(newRating),
      evidenceType,
      evidenceNote,
      confirmed: true,
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Update VA Rating</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="small">
            Verify the rating before saving. Changing it later can create a
            confusing client experience.
          </Alert>
          {isOutsideSubmittedStatus ? (
            <Alert variant="warning" className="small">
              This claim is currently {formatClaimStatus(claim?.claimStatus)}.
              Confirm this rating update is intentional before saving.
            </Alert>
          ) : null}

          <div className="mb-3">
            <div className="text-muted small">Current rating</div>
            <div className="fw-semibold">
              {currentRating == null ? 'Not set' : `${currentRating}%`}
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>New VA rating</Form.Label>
            <Form.Select
              value={newRating}
              onChange={(event) => setNewRating(event.target.value)}
              disabled={confirming || ratingOptions.length === 0}
              required
            >
              {ratingOptions.length === 0 ? (
                <option value="">No rating available</option>
              ) : null}
              {ratingOptions.map((rating) => (
                <option key={rating} value={rating}>
                  {rating === currentRatingNumber
                    ? `${rating}% (current, confirm no change)`
                    : `${rating}%`}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {confirmingNoChange ? (
            <Alert variant="info" className="small">
              This confirms the client&apos;s rating is unchanged. Nothing will
              be billed, and any open manual review for this claim will be
              marked handled.
            </Alert>
          ) : null}

          <Form.Group className="mb-3">
            <Form.Label>Evidence source</Form.Label>
            <Form.Select
              value={evidenceType}
              onChange={(event) => setEvidenceType(event.target.value)}
              disabled={confirming}
            >
              {EVIDENCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Evidence note</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={evidenceNote}
              onChange={(event) => setEvidenceNote(event.target.value)}
              placeholder="Where did this rating come from?"
              disabled={confirming}
            />
          </Form.Group>

          <Form.Check
            id="confirm-rating-checkbox"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
            disabled={confirming}
            label="I verified this VA rating and want to save it."
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="link" onClick={onHide} disabled={confirming}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={!canSubmit}>
            {confirming ? 'Saving...' : 'Save Rating'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ConfirmRatingModal;
